<template>
  <!-- Angepasste Darstellung für Timeline -->
  <div v-if="agendatype === 'timeline'" 
       :class="['timeline-card-wrapper', `et${ae.eventtype}`]" 
       @click="navigateAgenda" 
       class="pointer">
    <div :class="['card', 'h-100']">
       <div class="card-header timeline-header">
        <template v-if="isLoggedIn">
           <small class="text-muted action-icon" title="Bookmark" @click.stop="unsetFav" v-if="Include">
            <i class="fal fa-calendar-plus"></i>
          </small>
          <small class="text-muted action-icon" title="Bookmark" @click.stop="setFav" v-if="Exclude">
            <i class="fal fa-calendar-minus"></i>
          </small>
          <small class="text-muted action-icon" title="ICS Download" @click.stop="generateFileICS">
            <i class="fal fa-calendar-check"></i>
          </small>
        </template>
         <small class="time-display">{{formatedFromTime}} - {{formatedToTime}}</small>

         <small v-if="ae.stream" > 
           <IconWebcam title="Stream available" style="height: 1rem;" />
         </small>

      </div>
      <div class="card-body timeline-body">
         <p class="card-title bold">{{title}}</p>
         <p v-if="ae.subtitle" class="card-subtitle" v-html="ae.subtitle"></p>
         <p v-if="ae.hosted_by" class="card-text hosted-by">
           Hosted by: <span v-html="ae.hosted_by"></span>
         </p>
      </div>
       <div class="card-footer timeline-footer" v-if="ae.room || ae.location">
         <small class="text-muted">
           <span v-if="ae.room"><i class="fal fa-map-marker-alt"></i> <span v-html="ae.room"></span></span>
           <span v-if="ae.room && ae.location"> - </span>
           <span v-if="ae.location"><span v-html="ae.location"></span></span>
         </small>
       </div>
    </div>
  </div>

  <!-- Spezialfall: Mediathek/Video Ansicht -->
  <div v-else-if="agendatype === 'video' || route.name === 'Mediathek'" class="col-lg-4 col-md-6 col-12 mediathek">
    <div :class="`card mb-3 et${ae.eventtype}`">
       <!-- Kein Header für Mediathek -->
      <div class="card-body p-0"> <!-- Kein Padding im Body für Bild -->
        <a @click="navigateAgenda" class="pointer">
          <ImageWithFallback
              alt="Preview-Image"
              style="width: 100%;"
              :src="`/files/agenda_items/${ae.objvalueid}/preview.jpg`"
          ></ImageWithFallback>
          <div style="padding: 1rem;"> <!-- Padding um den Text unter dem Bild -->
             <p class="card-title bold">{{title}}</p>
             <!-- Ggf. weitere Infos hier? Aktuell nur Titel -->
          </div>
        </a>
      </div>
       <!-- Kein Footer für Mediathek -->
    </div>
  </div>

  <!-- Fallback/Default-Darstellung (Liste, Accordion etc.) -> Nimmt Timeline-Styling an -->
  <div v-else :class="{
    /* Spalten nur für Listenansicht, nicht im Accordion */
    'col-lg-9 col-12': agendatype !== 'accordion-timeline' 
  }">
    <!-- Card mit Timeline-Styling, aber ohne Wrapper/absolute Positionierung -->
    <div v-if="shouldShowEntry" :class="['card', 'mb-3', `et${ae.eventtype}`]" @click="navigateAgenda" class="pointer">
       <div class="card-header timeline-header"> <!-- Nutzt Timeline-Header-Klasse -->
        <template v-if="isLoggedIn">
           <small class="text-muted action-icon" title="Bookmark" @click.stop="unsetFav" v-if="Include">
            <i class="fal fa-calendar-plus"></i>
          </small>
          <small class="text-muted action-icon" title="Bookmark" @click.stop="setFav" v-if="Exclude">
            <i class="fal fa-calendar-minus"></i>
          </small>
          <small class="text-muted action-icon" title="ICS Download" @click.stop="generateFileICS">
            <i class="fal fa-calendar-check"></i>
          </small>
        </template>
         <small class="time-display">{{formatedFromTime}} - {{formatedToTime}}</small>

         <small v-if="ae.stream" > 
           <IconWebcam title="Stream available" style="height: 1rem;" />
         </small>
      </div>
      <div class="card-body timeline-body"> <!-- Nutzt Timeline-Body-Klasse -->
         <p class="card-title bold">{{title}}</p>
         <p v-if="ae.subtitle" class="card-subtitle" v-html="ae.subtitle"></p>
         <p v-if="ae.hosted_by" class="card-text hosted-by">
           Hosted by: <span v-html="ae.hosted_by"></span>
         </p>
      </div>
       <div class="card-footer timeline-footer" v-if="ae.room || ae.location"> <!-- Nutzt Timeline-Footer-Klasse -->
         <small class="text-muted">
           <span v-if="ae.room"><i class="fal fa-map-marker-alt"></i> <span v-html="ae.room"></span></span>
           <span v-if="ae.room && ae.location"> - </span>
           <span v-if="ae.location"><span v-html="ae.location"></span></span>
         </small>
       </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted  } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import _ from 'lodash'
import type { AgendaPoint } from '@/store/i/types'
import type { TranslatedText } from '@/store/types'
import { networkErrorText, type AppNetworkErrorOther } from '@/crud'
import { DateTime } from 'luxon'
import IconWebcam from '@/components/icons/IconWebcam.vue'
import ImageWithFallback from "@/components/ImageWithFallback.vue";

// Props
interface Props {
  ae: AgendaPoint
  type?: number
  agendatype?: string
}
const props = defineProps<Props>()

// Store, Router & Route
const store = useStore()
const router = useRouter()
const route = useRoute()

// Reactive State
const errorstring = ref<TranslatedText>({ de: '', en: '' })

// Computed Properties
const isLoggedIn = computed(() => 
  store.state.jwt !== ""
)

const Include = computed(() =>
    _.has(store.state.i.me.fav_agenda_items, props.ae.objvalueid)
)

const Exclude = computed(() =>
    !_.has(store.state.i.me.fav_agenda_items, props.ae.objvalueid)
)

const title = computed(() => {
  const text = _.replace(props.ae.title || "", /&#34;/g, '"')
  return _.replace(text, /&#39;/g, "'")
})

const formatedFromTime = computed(() => {
  try {
    // Zuerst versuchen wir es mit fromtime
    if (props.ae.fromtime) {
      const dt = DateTime.fromISO(props.ae.fromtime, {zone: 'Europe/Berlin'});
      if (dt.isValid) {
        return dt.toFormat('HH:mm');
      }
    }
    
    // Wenn fromtime nicht funktioniert, verwenden wir startdate
    if (props.ae.startdate) {
      const dt = DateTime.fromISO(props.ae.startdate, {zone: 'Europe/Berlin'});
      if (dt.isValid) {
        return dt.toFormat('HH:mm');
      }
    }
    
    return '--:--';
  } catch (e) {
    return '--:--';
  }
})

const formatedToTime = computed(() => {
  try {
    // Zuerst versuchen wir es mit totime
    if (props.ae.totime) {
      const dt = DateTime.fromISO(props.ae.totime, {zone: 'Europe/Berlin'});
      if (dt.isValid) {
        return dt.toFormat('HH:mm');
      }
    }
    
    // Wenn totime nicht funktioniert, verwenden wir enddate
    if (props.ae.enddate) {
      const dt = DateTime.fromISO(props.ae.enddate, {zone: 'Europe/Berlin'});
      if (dt.isValid) {
        return dt.toFormat('HH:mm');
      }
    }
    
    return '--:--';
  } catch (e) {
    return '--:--';
  }
})

// Computed property für die Anzeige-Bedingung
const shouldShowEntry = computed(() => {
  // Wenn wir im Speaker-Detail sind (agendatype === 'entry'), 
  // dann nur anzeigen wenn show_area === 1
  if (props.agendatype === 'entry') {
    return Number(props.ae.show_area) === 1
  }
  // In allen anderen Fällen immer anzeigen
  return true
})

// Methods
const navigateAgenda = () => {
  router.push({
    name: 'AgendaDetail',
    params: { agendaid: `${props.ae.objvalueid}` }
  })
}

const setFav = async () => {
  try {
    errorstring.value = { de: '', en: '' }
    const dpr = await store.dispatch("i/setDataFav", {
      objvalueid: props.ae.objvalueid
    })
    if (!dpr.sv) {
      errorstring.value = {
        de: "Feld konnte nicht gesetzt werden.",
        en: "Could not set Field."
      }
    }
  } catch (e) {
    errorstring.value = networkErrorText(e as AppNetworkErrorOther)
  }
}

const unsetFav = async () => {
  try {
    errorstring.value = { de: '', en: '' }
    const dpr = await store.dispatch("i/setDataUnFav", {
      objvalueid: props.ae.objvalueid
    })
    if (!dpr.sv) {
      errorstring.value = {
        de: "Feld konnte nicht entfernt werden.",
        en: "Could not remove Field."
      }
    }
  } catch (e) {
    errorstring.value = networkErrorText(e as AppNetworkErrorOther)
  }
}

const generateFileICS = () => {
  window.location.href = `/projects/dw/gmf/sources/scripts/portal.php?&action=generateFileICS&objvalueid=${props.ae.objvalueid}`
}

</script>

<style scoped>
.pointer {
  cursor: pointer;
}

/* Wrapper für Timeline-Card */
.timeline-card-wrapper {
  min-height: 4.7rem;
  position: absolute;
  z-index: 1;
  overflow: hidden;
  border-radius: 4px;
  /* box-shadow: 0 1px 3px rgba(0,0,0,0.1); */ /* Entfernt */
  border: 3px solid transparent; /* Rahmen direkt auf Wrapper */
  background-color: white; /* Hintergrund auf Wrapper */
}

/* Farben direkt auf Wrapper anwenden */
.timeline-card-wrapper.et1 { border-color: var(--event-type-1-color, #0d6efd); } 
.timeline-card-wrapper.et4 { border-color: var(--event-type-4-color, #198754); } 
.timeline-card-wrapper.et5 { border-color: var(--event-type-5-color, #ffc107); } 
.timeline-card-wrapper.et6 { border-color: var(--event-type-6-color, #0dcaf0); } 
.timeline-card-wrapper.et7 { border-color: var(--event-type-7-color, #dc3545); } 

/* NEU: Farben auch für normale Cards (nicht Timeline) */
.card.et1 { border-left: 5px solid var(--event-type-1-color, #0d6efd); } 
.card.et4 { border-left: 5px solid var(--event-type-4-color, #198754); } 
.card.et5 { border-left: 5px solid var(--event-type-5-color, #ffc107); } 
.card.et6 { border-left: 5px solid var(--event-type-6-color, #0dcaf0); } 
.card.et7 { border-left: 5px solid var(--event-type-7-color, #dc3545); } 

.timeline-card-wrapper .card {
  /* border: 3px solid transparent; */ /* Entfernt von innerer Card */
  border: none; /* Sicherstellen, dass kein Standardrahmen da ist */
  height: 100% !important;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: transparent; /* Innere Card transparent */
}

/* Kompaktere Styles für Timeline-Card */
.timeline-header,
.timeline-body,
.timeline-footer {
  padding: 0.2rem 0.4rem; /* Kleineres Padding */
  font-size: 0.75rem; /* Kleinere Schrift */
  line-height: 1.2;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa; /* Leichter Hintergrund für Header */
  border-bottom: 1px solid #dee2e6;
}

.timeline-header .action-icon {
  font-size: 0.9rem; /* Kleinere Icons */
  margin-left: 0.3rem;
}
.timeline-header .time-display {
    font-size: 0.7rem;
}

.timeline-body {
  flex-grow: 1; /* Body nimmt verfügbaren Platz ein */
  overflow-y: auto; /* Scrollen bei zu viel Inhalt */
}

.timeline-body .card-title {
  margin: 0 0 0.2rem 0;
  padding: 0;
  font-size: 0.8rem;
}
.timeline-body .card-subtitle {
  margin: 0 0 0.2rem 0;
  padding: 0;
  font-size: 0.7rem;
  color: #6c757d;
}
.timeline-body .hosted-by {
  margin: 0;
  padding: 0;
  font-size: 0.7rem;
}

.timeline-footer {
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa; /* Leichter Hintergrund für Footer */
  font-size: 0.7rem;
}


/* Bestehende Styles für andere Ansichten */
.mediathek img {
  width: 100%;
}

/* Entfernt oder angepasst, da .timeline-body/.timeline-header etc. jetzt globaler gelten */
/*
.card-title {
  margin-top: 1rem;
}

.card-body {
  height: auto;
  min-height: auto;
}
*/

.bold {
  font-weight: bold;
}
</style>
